/** @jsxImportSource @emotion/react */
import { StatePill } from 'components/lab/atoms/StatePill'
import {
  NotMailedModal,
  useMarkStatementReturned,
} from 'components/lab/billingPatientStatements/MailedStatementActions'
import {
  formatDateTime,
  formatMoney,
  formatPatientName,
} from 'components/lab/billingPatientStatements/statementFormat'
import React from 'react'
import { Modal } from 'react-bootstrap'
import { useGetStatementPrintRunQuery } from 'store/services/paperStatements'
import {
  billingStatementCycleStepLabel,
  STATEMENT_NOT_MAILED_REASON_LABEL,
  STATEMENT_PRINT_RUN_ITEM_STATUS_COLOR,
  STATEMENT_PRINT_RUN_ITEM_STATUS_LABEL,
  StatementNotMailedReason,
  StatementPrintRunItemDto,
  StatementPrintRunItemStatus,
} from 'store/types'

type Props = {
  // The run to show; the modal is shown while it is set.
  runId: string | null
  onHide: () => void
}

// What became of each envelope in a run, with the corrections a confirmed run
// still allows.
export const PrintRunDetailsModal: React.FC<Props> = ({ runId, onHide }) => {
  const [notMailedItemId, setNotMailedItemId] = React.useState<string | null>(null)
  const { data: run, isFetching } = useGetStatementPrintRunQuery(runId || '', { skip: !runId })
  const markReturned = useMarkStatementReturned()

  const renderOutcome = (item: StatementPrintRunItemDto) => {
    const status = item.status as StatementPrintRunItemStatus

    return (
      <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <StatePill
          color={STATEMENT_PRINT_RUN_ITEM_STATUS_COLOR[status] || '#6c757d'}
          label={STATEMENT_PRINT_RUN_ITEM_STATUS_LABEL[status] || status}
        />
        {item.mailedAt && <span className="textGray" css={{ fontSize: 12 }}>Mailed {formatDateTime(item.mailedAt)}</span>}
        {item.returnedAt && <span className="textGray" css={{ fontSize: 12 }}>Returned {formatDateTime(item.returnedAt)}</span>}
        {item.notMailedReason && (
          <span className="textGray" css={{ fontSize: 12 }}>
            {STATEMENT_NOT_MAILED_REASON_LABEL[item.notMailedReason as StatementNotMailedReason] || item.notMailedReason}
            {item.note ? `: ${item.note}` : ''}
          </span>
        )}
        {item.statusChangedAt && (
          <span className="textGray" css={{ fontSize: 12 }}>
            Changed {formatDateTime(item.statusChangedAt)}{item.statusChangedByName ? ` by ${item.statusChangedByName}` : ''}
          </span>
        )}
      </div>
    )
  }

  return (
    <>
      <Modal show={!!runId && !notMailedItemId} onHide={onHide} centered size="xl">
        <Modal.Header>
          <Modal.Title>Print run {formatDateTime(run?.printedAt)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="textGray">
            Printed by {run?.printedByName || '--'}
            {run?.confirmedAt && ` · Confirmed ${formatDateTime(run.confirmedAt)} by ${run.confirmedByName || '--'}`}
            {run?.cancelledAt && ` · Cancelled ${formatDateTime(run.cancelledAt)} by ${run.cancelledByName || '--'}`}
          </p>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Balance</th>
                  <th>Step</th>
                  <th>Outcome</th>
                  <th style={{ width: 130 }} />
                </tr>
              </thead>
              <tbody>
                {isFetching && (
                  <tr>
                    <td colSpan={5} className="text-center">Loading...</td>
                  </tr>
                )}
                {!isFetching && (run?.items || []).map((item: StatementPrintRunItemDto) => (
                  <tr key={item.id}>
                    <td>{formatPatientName(item.patientLastName, item.patientFirstName)}</td>
                    <td>{formatMoney(item.balance)}</td>
                    <td>{billingStatementCycleStepLabel(item.stepKey)}</td>
                    <td>{renderOutcome(item)}</td>
                    <td>
                      {item.status === StatementPrintRunItemStatus.MAILED && (
                        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <button className="btn btn-link p-0" onClick={() => setNotMailedItemId(item.id)}>
                            Not mailed
                          </button>
                          <button className="btn btn-link p-0" onClick={() => markReturned(item.id)}>
                            Returned
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={onHide}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
      <NotMailedModal itemId={notMailedItemId} onHide={() => setNotMailedItemId(null)} />
    </>
  )
}
